import React from "react";
import useSWR from "swr";

// NOTE: This LoaderCache singleton predates and duplicates SWR's own cache;
// it is retained only because other screens still mount <Loader>. New code
// (e.g. ConfirmersAppointments) uses `fetcher` directly with SWR and does not
// rely on this cache.
class LoaderCache {
  private cache: Record<string, unknown> = {};

  constructor() {
    document.addEventListener("turbo:load", () => {
      this.cache = {};
    });
  }

  set(url: string, data: unknown): void {
    this.cache[this.getKey(url)] = data;
  }

  get(url: string): unknown {
    return this.cache[this.getKey(url)];
  }

  has(url: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.cache, this.getKey(url));
  }

  // Full URL: loaders pointing at the same path with different query params
  // (e.g. the confirmers appointment groups) must not share cached data.
  getKey(url: string): string {
    return url;
  }
}

const cache = new LoaderCache();

export const fetcher = <T = unknown,>(url: string, options: RequestInit = {}): Promise<T> =>
  fetch(url, options).then((res) => {
    if (!res.ok) throw new Error(`Request to ${url} failed with status ${res.status}`);

    return res.json() as Promise<T>;
  });

export interface LoaderProps {
  url: string;
  as?: string;
  children: React.ReactNode;
}

const Loader = ({ url, as = "data", children }: LoaderProps): JSX.Element | null => {
  const swr = useSWR(url, fetcher, { revalidateOnFocus: false });
  let data = swr.data;
  const error = swr.error;

  if (cache.has(url) && data === undefined) {
    data = cache.get(url);
  } else {
    if (error) return <div>Error</div>; // TODO: do something sensible here
    if (data === undefined) return <div>Loading...</div>;

    cache.set(url, data);
  }

  return (
    <>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child, { [as]: data } as React.Attributes) : child
      )}
    </>
  );
};

export default Loader;
